import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import { Helmet as MetaTags } from 'react-helmet';
import MapsContainer, {
  type Client_Feature,
  type LoadArgs,
} from './components/MapsContainer';
import MapBoxContext from '~/contexts/MapBoxContext';
import styled, { css } from 'styled-components';
import useMeasure from '~/hooks/useMeasure';
import { FOOTER_HEIGHT } from '~/components/organism/NavigationFrame/components/Footer';
import EntryCard from '~/components/molecule/EntryCard';
import useOffice from '~/hooks/useOffice';
import { equals, flatten, isNil, lensIndex, over, pluck, reject } from 'ramda';
import type { Feature, GeoJsonProperties, Geometry } from 'geojson';
import { Body, Heading4, Variant } from '~/components/atom/Typography';
import featuresToInput from './utils/featuresToInput';
import { scrollBarStyles } from '~/components/molecule/OverflowScrollWrapper';
import OutputAccordion from '~/components/organism/OutputAccordion';
import CallToActionBlock from '~/components/organism/CallToActionBlock';
import JustificationContainer from '~/components/atom/JustificationContainer';
import {
  useUpdateOfficeMutation,
  type Office_Update,
  type WorkingAreas,
} from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import removeTypenames from '~/util/removeTypenames';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import Catalog from '~/Catalog';
import getClientWorkingAreas, {
  type Client_WorkingArea,
  type Client_WorkingAreas,
} from './utils/getClientWorkingAreas';
import getCurrentWorkingAreas from './utils/getCurrentWorkingAreas';
import { v4 as uuidv4 } from 'uuid';
import useKeybinding from '~/hooks/useKeybinding';
import useOffices from '~/hooks/useOffices';
import ToggleCheckbox from '~/components/molecule/ToggleCheckbox';
import type ExtendedMapboxDraw from './components/MapsContainer/components/Draw/ExtendedMapboxDraw';
import type { WorkingAreaType } from './components/MapsContainer/components/Draw';
import Divider from '~/components/atom/Divider';
import AddWorkingAreaContainer from './components/AddWorkingAreaContainer';
import FloatingSaveBar from './components/FloatingSaveBar';
import AssignUserDropdown from './components/AssignUserDropdown';
import MissingUsersErrorMessage from './components/MissingUsersErrorMessage';
import TypographyWithHelpLink from '~/components/molecule/TypographyWithHelpLink';
import { LeftAsideContainer, BaseAside } from './style';
import createPageTitle from '~/util/createPageTitle';
import getPreset from './utils/getPreset';
import hasSelfIntersectingWorkingAreas from './utils/hasSelfIntersectingWorkingAreas';
import { HELP_DESK_LINK } from '~/constants';

type Props = RouteComponentProps<{ officeId: string }>;

// Visual styling mappings for different working area types
const workingAreaSentiment: {
  [key in WorkingAreaType]: 'positive' | 'negative';
} = {
  inclusive: 'positive',
  exclusive: 'negative',
};

const workingAreaVariant: {
  [key in WorkingAreaType]: 'success' | 'danger';
} = {
  inclusive: 'success',
  exclusive: 'danger',
};

const text = {
  pageTitle: 'Werkgebied',
  title: 'Werkgebied',
  subHeading: 'Werkgebieden',
  selectedAreas: 'Geselecteerde werkgebieden',
  noFeatures: 'Klik op de kaart om een werkgebied te defineren',
  exclusive: 'Uitgesloten werkgebieden',
  inclusive: 'Werkgebieden',
  helpLink: `${HELP_DESK_LINK}/nl/articles/9565399-werkgebieden-instellen`,
  workingAreaLabel: {
    inclusive: 'inclusief',
    exclusive: 'exclusief',
  },
  description: `Teken het werkgebied op de kaart. Klik op een punt in het werkgebied om de selectie te voltooien. Klik op 'Klaar met selecteren' om het proces af te ronden.

Gebruik het werkgebied in Automation om contacten automatisch aan de juiste vestiging toe te wijzen.`,
  showOtherOffices: 'Toon werkgebieden van andere vestigingen',

  userWasRemovedError:
    'Een van de geselecteerde gebruikers was verwijderd. Ververs de pagina en probeer het opnieuw.',
  selfIntersectionHeader: 'Werkgebied kruist zichzelf',
  selfIntersectionDescription:
    'Pas de vorm aan zodat de lijnen elkaar niet kruisen. Daarna kun je opslaan.',
};

const defaultWorkingAreas: WorkingAreas = {
  __typename: 'WorkingAreas',
  inclusive: [],
  exclusive: [],
};

/**
 * WorkingArea Component - Manages geographic working areas for office locations
 *
 * Features:
 * - Interactive map drawing of inclusive/exclusive working areas
 * - User assignment to working areas for automation routing
 * - Real-time collaboration with other offices' areas displayed
 * - Keyboard shortcuts for area manipulation (ESC, Delete/Backspace)
 * - Preset functionality to select entire Netherlands as working area
 */
const WorkingArea: React.FCC<Props> = ({ officeId }: { officeId: string }) => {
  // Reference for calculating floating save bar offset to prevent UI overlap
  const saveBarRef = useRef<HTMLDivElement | null>(null);
  const saveBarOffsetY = saveBarRef.current?.getBoundingClientRect().height;

  const office = useOffice(officeId);
  const offices = useOffices({}).filter(o => o.id !== officeId);

  // Controls visibility of accordion sections for different working area types
  const [asideToggleState, setAsideToggleState] = useState<{
    [key in WorkingAreaType]: boolean;
  }>({
    inclusive: false,
    exclusive: false,
  });

  const addToast = useAddToast();
  const { id: accountId } = useCurrentAccount();
  const [updateOffice, { loading }] = useUpdateOfficeMutation();

  // Working areas state management - tracks original vs current state for change detection
  const [initialWorkingAreas, setInitialWorkingAreas] =
    useState<Client_WorkingAreas>([]);
  const [workingAreas, setWorkingAreas] = useState<Client_WorkingAreas>([]);

  // Controls visibility of other offices' working areas on the map
  const [showReadOnly, setShowReadOnly] = useState<boolean>(true);

  // Read-only working areas from other offices for reference/context
  const readOnlyFeatures: Client_WorkingAreas = flatten(
    offices.map(o => {
      const clientWorkingAreas = getClientWorkingAreas({
        workingAreas: o.workingAreas ?? defaultWorkingAreas,
        officeId: o.id,
        readOnly: true,
      });

      return clientWorkingAreas;
    }),
  );

  // Map drawing state - tracks what type of area is currently being drawn
  const [activeDrawType, setActiveDrawType] = useState<WorkingAreaType | null>(
    null,
  );
  const [selectedWorkingAreas, setSelectedWorkingAreas] = useState<
    Array<string>
  >([]);

  const { bounds, ref } = useMeasure();
  const drawRef = useRef<ExtendedMapboxDraw>();

  // Resets map drawing state and clears all drawn features
  const onReset = () => {
    if (drawRef.current) {
      drawRef.current.deleteAll();
    }
    setActiveDrawType(null);
  };

  // Keyboard shortcut: ESC cancels current drawing operation
  useKeybinding({
    action: 'keydown',
    keys: ['esc'],
    callback: () => {
      if (activeDrawType !== null) {
        setActiveDrawType(null);
        if (drawRef.current) drawRef.current.cancelSelection();
      }
    },
  });

  // Keyboard shortcut: Backspace/Delete removes a selected vertex only.
  // Whole working areas are deleted via the sidebar card button.
  useKeybinding({
    action: 'keydown',
    keys: ['backspace', 'del'],
    callback: () => {
      const draw = drawRef.current;
      const currentlySelectedWorkingAreas = draw?.getSelected().features;
      const selectedPoints = draw?.getSelectedPoints().features;

      if (draw && selectedPoints && selectedPoints.length > 0) {
        draw.trash();
        return;
      }

      if (currentlySelectedWorkingAreas) {
        onDelete(currentlySelectedWorkingAreas);
        onReset();
      }
    },
  });

  // Initialize working areas from server data on component mount
  useEffect(() => {
    if (initialWorkingAreas.length === 0 || workingAreas.length === 0) {
      const initial = getClientWorkingAreas({
        workingAreas: office?.workingAreas ?? defaultWorkingAreas,
        officeId,
      });
      setInitialWorkingAreas(initial);
      setWorkingAreas(initial);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Callback for when MapBox draw instance is ready
  const onLoaded = useCallback(({ draw }: LoadArgs) => {
    drawRef.current = draw;
  }, []);

  // Removes working areas from state when deleted from map
  const onDelete = useCallback((featuresToDelete: Array<Feature>) => {
    setWorkingAreas(prevAreas =>
      reject(
        (area: Client_WorkingArea) =>
          featuresToDelete.some(({ id }) => id === area.feature.id),
        prevAreas,
      ),
    );
  }, []);

  // Updates specific working area properties (geometry or user assignment)
  const onUpdateWorkingArea = ({
    featureId,
    updatedValue,
  }: {
    featureId: string;
    updatedValue: { userId: string | null } | { feature: Client_Feature };
  }) => {
    setWorkingAreas(prev => {
      const currentFeatureIndex = prev.findIndex(
        area => area.feature.id === featureId,
      );
      const arrayLens = lensIndex<{ feature: Feature }>(currentFeatureIndex);
      const prevWorkingArea = prev[currentFeatureIndex];

      return over(
        arrayLens,
        () => ({ ...prevWorkingArea, ...updatedValue }),
        prev,
      );
    });
  };

  // Handles geometry updates when features are modified on the map
  const onUpdate = (features: Array<Feature>) => {
    features.forEach(feature => {
      if (feature && feature.id) {
        onUpdateWorkingArea({
          featureId: feature.id as string,
          updatedValue: {
            feature: {
              ...feature,
              properties: { ...feature.properties },
            },
          },
        });
      }
    });
  };

  // Adds newly drawn working areas to state and shows them in the sidebar
  const onCreate = useCallback(([feature]: Array<Feature>) => {
    setWorkingAreas(prev => [
      ...prev,
      {
        userId: null,
        feature: {
          ...feature,
          id: `${officeId}-${uuidv4()}`,
          properties: { ...feature.properties },
        },
      },
    ]);

    // Auto-expand relevant accordion section for new features
    if (feature.properties)
      setAsideToggleState(prev => ({
        ...prev,
        [feature.properties?.inclusive ? 'inclusive' : 'exclusive']: true,
      }));

    onReset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persists working areas to server and handles success/error states
  const onSave = async (rawFeatures: Client_WorkingAreas) => {
    if (hasSelfIntersectingWorkingAreas(rawFeatures)) {
      return;
    }

    const featuresInput = featuresToInput(rawFeatures);

    const update: Office_Update = removeTypenames({
      workingAreas: featuresInput,
    });

    const { data, errors } = await updateOffice({
      variables: { accountId, officeId, update },
    });

    if (errors && errors.length > 0) {
      return addToast([formatToastMessage(text.userWasRemovedError, 'danger')]);
    }

    if (data && data.updateOffice.workingAreas) {
      const updatedFeatures = getClientWorkingAreas({
        officeId,
        workingAreas: data.updateOffice.workingAreas,
      });

      // Clear map and reset state to prevent duplication issues
      onReset();

      // Delay state update to ensure map is cleared first
      setTimeout(() => {
        setInitialWorkingAreas(updatedFeatures);
        setWorkingAreas(updatedFeatures);
      }, 100);

      setSelectedWorkingAreas([]);
      return addToast([
        formatToastMessage(Catalog.genericSuccessMessage, 'success'),
      ]);
    }
  };

  // Handles feature selection on map and updates UI accordingly
  const onSelect = (features: Array<Feature<Geometry, GeoJsonProperties>>) => {
    setActiveDrawType(null);

    if (features.length === 0) return setSelectedWorkingAreas([]);

    setSelectedWorkingAreas(
      features
        .map(feature => feature.id)
        .filter((id): id is string => typeof id === 'string'),
    );

    // Auto-expand accordion sections for selected features
    features.forEach(feature => {
      if (feature.properties) {
        setAsideToggleState(prev => ({
          ...prev,
          [feature.properties?.inclusive === true ? 'inclusive' : 'exclusive']:
            true,
        }));
      }
    });
  };

  const currentWorkingAreas = getCurrentWorkingAreas(workingAreas);
  const hasChanges = !equals(initialWorkingAreas, workingAreas);
  const hasSelfIntersectingPolygon =
    hasSelfIntersectingWorkingAreas(workingAreas);

  const currentUserIds = pluck('userId', currentWorkingAreas.inclusive).filter(
    (id): id is string => !isNil(id),
  );

  return (
    <MapBoxContext.Provider value={process.env.MAPBOX_ACCESS_TOKEN as string}>
      <MetaTags>
        <title>{createPageTitle(text.pageTitle)}</title>
      </MetaTags>

      {/* Main map container with dynamic sizing */}
      <MapLimitContainer ref={ref}>
        <MapsContainer
          officeId={officeId}
          features={[
            ...workingAreas.map(wa => wa.feature),
            ...(showReadOnly ? readOnlyFeatures.map(wa => wa.feature) : []),
          ]}
          height={bounds.height - FOOTER_HEIGHT}
          width={bounds.width}
          onLoaded={onLoaded}
          onCreate={onCreate}
          onDelete={onDelete}
          onUpdate={onUpdate}
          onSelect={onSelect}
        />
      </MapLimitContainer>

      {/* Floating save/cancel bar with change detection */}
      <FloatingSaveBar
        onSave={async () => onSave(workingAreas)}
        onCancel={() => {
          setWorkingAreas(initialWorkingAreas);
          setSelectedWorkingAreas([]);
          onReset();
        }}
        loading={loading}
        disabled={loading || hasChanges === false}
        saveDisabled={hasSelfIntersectingPolygon}
        ref={saveBarRef}
      />

      {/* Right sidebar with working area management */}
      <AsideContainer $offsetY={saveBarOffsetY}>
        <JustificationContainer width="100%" gap="base" direction="column">
          <TypographyWithHelpLink
            help={{ link: text.helpLink }}
            TypographyComponent={Heading4}
          >
            {text.subHeading}
          </TypographyWithHelpLink>

          <Body size="base" margin={[null]}>
            {text.description}
          </Body>

          <ToggleCheckbox
            value={showReadOnly}
            label={text.showOtherOffices}
            onChange={() => setShowReadOnly(prev => !prev)}
            containerProps={{
              direction: 'row',
              width: '100%',
              justification: 'space-between',
              align: 'center',
              gap: 'xxs',
            }}
            size="small"
          />
          <MissingUsersErrorMessage
            officeId={officeId}
            currentUserIds={currentUserIds}
          />
          {hasSelfIntersectingPolygon && (
            <CallToActionBlock
              icon={{ background: { group: 'danger' }, name: 'triangle' }}
              header={text.selfIntersectionHeader}
              description={text.selfIntersectionDescription}
              align="center"
            />
          )}
        </JustificationContainer>

        <Divider margin={['l', null]} />

        {/* Working areas list with expandable sections */}
        <EntryList>
          <Heading4 margin={[null]} variant={Variant.primary}>
            {text.selectedAreas}
          </Heading4>
          {Object.keys(currentWorkingAreas).map((key: WorkingAreaType) => (
            <OutputAccordion
              key={key}
              header={{ label: text[key] }}
              variant={workingAreaVariant[key]}
              onChange={() =>
                setAsideToggleState(prev => ({
                  ...prev,
                  [key]: !prev[key],
                }))
              }
              isOpen={asideToggleState[key]}
            >
              {currentWorkingAreas[key].length === 0 && (
                <Heading4 fontWeight="medium">{text.noFeatures}</Heading4>
              )}
              <JustificationContainer direction="column" gap="m">
                {currentWorkingAreas[key].map(
                  (workingArea: Client_WorkingArea, index) => (
                    <EntryCard
                      key={`${workingArea.feature.id}-${index}`}
                      sentiment={workingAreaSentiment[key]}
                      title={`${office?.name} werkgebied #${index + 1}`}
                      description={
                        key === 'inclusive' ? (
                          <AssignUserDropdown
                            onUpdateWorkingArea={onUpdateWorkingArea}
                            workingArea={workingArea}
                            officeId={officeId}
                          />
                        ) : null
                      }
                      selected={selectedWorkingAreas?.includes(
                        workingArea.feature.id as string,
                      )}
                      buttons={[
                        {
                          icon: 'delete',
                          appearance: 'danger',
                          onClick: () => {
                            if (drawRef.current) {
                              drawRef.current.delete(
                                workingArea.feature.id as string,
                              );
                              onDelete([workingArea.feature]);
                            }
                          },
                        },
                      ]}
                      onClick={() => {
                        if (drawRef.current && workingArea.feature.id)
                          drawRef.current.toggleSelection(
                            workingArea.feature.id,
                          );
                      }}
                    />
                  ),
                )}
              </JustificationContainer>
            </OutputAccordion>
          ))}
        </EntryList>
      </AsideContainer>

      {/* Left sidebar with drawing tools */}
      <LeftAsideContainer>
        <AddWorkingAreaContainer
          addPreset={({ preset, workingAreaType }) => {
            const presetFeature = getPreset({
              preset,
              officeId,
              workingAreaType,
            });
            onCreate([presetFeature]);

            const [areaType] = Object.keys(workingAreaType) as [
              WorkingAreaType,
            ];

            setActiveDrawType(areaType);
          }}
          activeDrawType={activeDrawType}
          onStopSelection={() => {
            setActiveDrawType(null);
            if (drawRef.current) drawRef.current.stopSelection();
          }}
          onStartSelection={type => {
            if (drawRef.current) {
              setActiveDrawType(type);
              drawRef.current.startSelection(type);
            }
          }}
        />
      </LeftAsideContainer>
    </MapBoxContext.Provider>
  );
};
const EntryList = styled.div<{}>(
  ({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: ${theme.space('base')};
  `,
);

const MapLimitContainer = styled.div<{}>`
  position: absolute;
  height: 100%;
  width: 100%;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
`;

const AsideContainer = styled(BaseAside)<{ $offsetY?: number }>(
  ({ theme, $offsetY }) => css`
    top: calc(${$offsetY || 0}px);
    bottom: ${theme.space('xxxxl')};
    display: flex;
    flex-direction: column;
    overflow-x: auto;
    margin-top: ${theme.space('l')};

    ${scrollBarStyles}
  `,
);

export default WorkingArea;
